import React, { memo, useContext, useMemo } from 'react';
import styled from '@emotion/styled';

import { PreviewComponentProps } from '../../EmailTemplatePreview';
import EditorContext, { EditorContextProps } from '../../../Editor/Context';
import Cta from '../Components/Cta';
import ChatBubble from '../Components/ChatBubble';
import VideoPreview from '../Components/VideoPreview';
import Avatar from '../Components/Avatar';
import Disclaimer from '../Components/Disclaimer';
import Footer from '../Components/Footer';
import Banner from '../Components/Banner';
import { useVmPlan } from '~/react-hooks/use-plans';

const Container = styled.div`
  margin: 0 auto;
  font-size: 10px;
  max-width: 60em;
  font-family: Lato, Helvetica, Arial, sans-serif;
`;

const Spacer = styled.td<{ minWidth?: number }>`
  padding: 0;
  min-width: 2em;
  @media (min-width: 1024px) {
    min-width: ${(props) => props.minWidth || 14}em;
  }
`;

const SentBy = ({ name }: Pick<PreviewComponentProps, 'name'>) => (
  <td
    align='center'
    className='text-[#60666D] p-0 text-center leading-10 text-base w-auto'
  >
    Sent by {name}
  </td>
);

const HtmlBody = memo(
  ({
    name,
    image,
    children,
    playerImage,
    theme,
    hidePreviewTag,
    bannerContent,
    disclaimerContent,
  }: PreviewComponentProps) => {
    const editorContext: EditorContextProps = useContext(EditorContext);
    const { isFreePlan } = useVmPlan();

    const { preview } = editorContext;
    const isMobile = useMemo(() => {
      return preview === 'mobile';
    }, [preview]);

    return (
      <>
        {name && (
          <Banner sender={name} className='mb-12' content={bannerContent} />
        )}
        <Container>
          <table>
            <tbody>
              <tr>
                <Spacer minWidth={isMobile ? 2 : 14} />
                <div className='w-full flex'>
                  <Avatar image={image} />
                  <ChatBubble chatBubbleClassName='px-3 pt-2 pb-0 text-neutral-700'>
                    {children}
                  </ChatBubble>
                </div>
                <Spacer minWidth={isMobile ? 2 : 14} />
              </tr>
              <tr>
                <Spacer minWidth={isMobile ? 2 : 14} />
                <SentBy name={name} />
                <Spacer minWidth={isMobile ? 2 : 14} />
              </tr>
              <tr>
                <Spacer minWidth={isMobile ? 2 : 14} />
                <VideoPreview
                  thumbnail={playerImage}
                  hidePreviewTag={hidePreviewTag}
                />
                <Spacer minWidth={isMobile ? 2 : 14} />
              </tr>
              <tr>
                <Spacer minWidth={isMobile ? 2 : 14} />
                <Cta
                  text='Watch the video I recorded for you'
                  textColor={theme?.secondaryColor}
                  bgColor={theme?.primaryColor}
                />
                <Spacer minWidth={isMobile ? 2 : 14} />
              </tr>
            </tbody>
          </table>
          <Disclaimer className='pt-11' contents={disclaimerContent} />
          {isFreePlan && <Footer />}
        </Container>
      </>
    );
  }
);

HtmlBody.displayName = 'HtmlBody';

export default HtmlBody;
